import React, { useState, useEffect, useMemo } from 'react';
import { 
    Dropdown, 
    TextField, 
    IDropdownOption, 
    ITextFieldProps,
    Label,
    Stack,
    IStackTokens
} from '@fluentui/react';
import { getCodeList } from 'country-list';
import { getCountryCallingCode, isValidPhoneNumber, isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/de.json';
import ReactCountryFlag from 'react-country-flag';

// Remove the inline module declaration for react-country-flag

interface PhoneInputProps {
    value?: string;
    onChange?: (value: string) => void;
    onValidationChange?: (isValid: boolean) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    errorMessage?: string;
    className?: string;
    defaultCountry?: string; // ISO 3166-1 alpha-2 country code (e.g., 'DE', 'US')
    showValidation?: boolean;
}

interface CountryOption extends IDropdownOption {
    callingCode: string;
    flag?: string;
}

const stackTokens: IStackTokens = {
    childrenGap: 8,
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
    value = '',
    onChange,
    onValidationChange,
    label,
    required = false,
    disabled = false,
    placeholder,
    errorMessage,
    className,
    defaultCountry = 'DE',
    showValidation = true,
}) => {
    const { t } = useTranslation();
    const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountry);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [validationError, setValidationError] = useState<string>('');
    const [isValid, setIsValid] = useState<boolean>(false);

    // Initialize i18n-iso-countries with German
    countries.registerLocale(require('i18n-iso-countries/langs/de.json'));
    
    // Get country data
    const countryList = getCodeList();
    
    // Create country options with calling codes
    const countryOptions: CountryOption[] = useMemo(() => {
        return Object.keys(countryList).map((countryCode) => {
            // Get German country name
            const germanCountryName = countries.getName(countryCode.toUpperCase(), 'de') || countryList[countryCode];
            let callingCode = '';
            
            try {
                callingCode = getCountryCallingCode(countryCode.toUpperCase() as any);
            } catch (error) {
                // Some countries might not have calling codes in libphonenumber-js
                callingCode = '';
            }

            return {
                key: countryCode.toUpperCase(),
                text: `${germanCountryName} (+${callingCode})`,
                callingCode,
                flag: countryCode.toUpperCase(),
                data: {
                  countryCode: countryCode.toUpperCase(),
                  flag: countryCode.toUpperCase(),
                }
            };
        }).filter(option => option.callingCode) // Only include countries with calling codes
          .sort((a, b) => a.text.localeCompare(b.text, 'de'));
    }, [countryList]);

    // Custom render for dropdown options with flag
    const onRenderOption = (option?: IDropdownOption): JSX.Element => {
      if (!option) return <></>;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ReactCountryFlag countryCode={option.key as string} svg style={{ width: '1.5em', height: '1.5em' }} />
          <span>{option.text}</span>
        </div>
      );
    };

    // Custom render for selected item with flag
    const onRenderTitle = (options?: IDropdownOption[]): JSX.Element => {
      if (!options || options.length === 0) return <></>;
      const option = options[0];
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ReactCountryFlag countryCode={option.key as string} svg style={{ width: '1.5em', height: '1.5em' }} />
          <span>{option.text}</span>
        </div>
      );
    };

    // Parse initial value if provided
    useEffect(() => {
        if (value) {
            try {
                const parsed = parsePhoneNumber(value);
                if (parsed && parsed.country) {
                    setSelectedCountry(parsed.country);
                    setPhoneNumber(parsed.nationalNumber || '');
                }
            } catch (error) {
                // If parsing fails, try to extract country code from the beginning
                const match = value.match(/^\+(\d+)\s*(.+)$/);
                if (match) {
                    const [, callingCode, number] = match;
                    const country = countryOptions.find(opt => opt.callingCode === callingCode);
                    if (country) {
                        setSelectedCountry(country.key as string);
                        setPhoneNumber(number);
                    }
                }
            }
        }
    }, [value, countryOptions]);

    // Validate phone number
    useEffect(() => {
        if (!phoneNumber.trim()) {
            setValidationError('');
            setIsValid(false);
            onValidationChange?.(false);
            return;
        }

        const fullNumber = `+${getCountryCallingCode(selectedCountry as any)}${phoneNumber}`;
        
        let error = '';
        let valid = false;

        try {
            if (!isPossiblePhoneNumber(fullNumber)) {
                error = t('phoneInput.invalidFormat', 'Invalid phone number format');
            } else if (!isValidPhoneNumber(fullNumber)) {
                error = t('phoneInput.invalidNumber', 'Invalid phone number');
            } else {
                valid = true;
            }
        } catch (error) {
            error = t('phoneInput.invalidFormat', 'Invalid phone number format');
        }

        setValidationError(error);
        setIsValid(valid);
        onValidationChange?.(valid);
    }, [phoneNumber, selectedCountry, onValidationChange, t]);

    // Update parent component when value changes
    useEffect(() => {
        if (phoneNumber && selectedCountry) {
            try {
                const fullNumber = `+${getCountryCallingCode(selectedCountry as any)}${phoneNumber}`;
                onChange?.(fullNumber);
            } catch (error) {
                // Handle error silently
            }
        } else {
            onChange?.('');
        }
    }, [phoneNumber, selectedCountry, onChange]);

    const handleCountryChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            setSelectedCountry(option.key as string);
        }
    };

    const handlePhoneNumberChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        // Only allow digits, spaces, dashes, and parentheses
        const cleanedValue = newValue?.replace(/[^\d\s\-\(\)]/g, '') || '';
        setPhoneNumber(cleanedValue);
    };

    const selectedCountryOption = countryOptions.find(opt => opt.key === selectedCountry);

    return (
        <div className={className}>
            {label && (
                <Label required={required}>
                    {label}
                </Label>
            )}
            <Stack horizontal tokens={stackTokens} styles={{ root: { alignItems: 'end' } }}>
                <Dropdown
                    label=""
                    selectedKey={selectedCountry}
                    options={countryOptions}
                    onChange={handleCountryChange}
                    disabled={disabled}
                    styles={{
                        root: { width: 320 },
                        dropdown: { width: 320 },
                        callout: { width: 320 }
                    }}
                    placeholder={t('phoneInput.selectCountry', 'Select country')}
                    onRenderOption={onRenderOption}
                    onRenderTitle={onRenderTitle}
                />
                <TextField
                    label=""
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    disabled={disabled}
                    placeholder={placeholder || t('phoneInput.enterPhoneNumber', 'Enter phone number')}
                    styles={{
                        root: { flex: 1 },
                        fieldGroup: { minWidth: 200 }
                    }}
                />
            </Stack>
            {showValidation && phoneNumber && (
                <div style={{ 
                    fontSize: '12px', 
                    marginTop: '4px',
                    color: isValid ? '#107c10' : '#d13438'
                }}>
                    {isValid 
                        ? t('phoneInput.validNumber', '✓ Valid phone number')
                        : t('phoneInput.invalidNumber', '✗ Invalid phone number')
                    }
                </div>
            )}
        </div>
    );
};

export default PhoneInput; 